import { useTranslation } from 'react-i18next';

export default function ProfitMechanism() {
  const { t } = useTranslation();
  return (
    <section id="profit-mechanism" className="py-16 md:py-24 relative overflow-hidden" style={{ background: 'linear-gradient(180deg, #100b1e 0%, #0c0814 100%)' }}>
      <div className="absolute inset-0 grid-bg pointer-events-none opacity-50" />

      <div className="max-w-7xl mx-auto px-4 md:px-6 relative z-10">
        {/* Header */}
        <div className="text-center mb-12 md:mb-20">
          <div className="badge-purple mb-6">
            <i className="ri-money-dollar-circle-line text-xs"></i>
            {t('profitMechanism.badge')}
          </div>
          <h2 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-5 leading-tight font-heading">
            {t('profitMechanism.title1')}
            <br />
            <span className="text-gradient-aurora">{t('profitMechanism.title2')}</span>
          </h2>
          <p className="text-base md:text-lg text-white/45 max-w-3xl mx-auto leading-relaxed">
            {t('profitMechanism.desc')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mb-12 md:mb-20">
          {/* Mechanism 1 */}
          <div className="premium-card premium-card-hover rounded-2xl p-6 md:p-8">
            <div className="flex items-start gap-4 mb-5 md:mb-6">
              <div className="w-12 md:w-14 h-12 md:h-14 rounded-xl bg-purple-400/10 border border-purple-400/20 flex items-center justify-center flex-shrink-0">
                <i className="ri-line-chart-line text-2xl text-purple-400"></i>
              </div>
              <div>
                <h3 className="text-xl md:text-2xl font-bold text-white mb-1 font-heading">{t('profitMechanism.m1_title')}</h3>
                <p className="text-xs md:text-sm text-purple-400 font-medium">{t('profitMechanism.m1_sub')}</p>
              </div>
            </div>
            <p className="text-white/50 mb-5 leading-relaxed text-sm md:text-base">
              {t('profitMechanism.m1_desc')}
            </p>
            <div className="bg-white/[0.03] border border-purple-500/10 rounded-xl p-4 md:p-5 mb-5">
              <div className="flex justify-between items-center mb-3">
                <span className="text-sm text-white/40">{t('profitMechanism.m1_marketImplied')}</span>
                <span className="text-2xl md:text-3xl font-bold text-white">60%</span>
              </div>
              <div className="flex justify-between items-center mb-3">
                <span className="text-sm text-white/40">{t('profitMechanism.m1_aiTrue')}</span>
                <span className="text-2xl md:text-3xl font-bold text-purple-400">72%</span>
              </div>
              <div className="h-px bg-purple-500/10 my-3"></div>
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-purple-400">{t('profitMechanism.m1_probDiff')}</span>
                <span className="text-3xl md:text-4xl font-bold text-purple-400">+12%</span>
              </div>
            </div>
            <div className="flex items-center gap-2 text-xs text-white/30">
              <i className="ri-information-line"></i>
              <span>{t('profitMechanism.m1_note')}</span>
            </div>
          </div>

          {/* Mechanism 2 */}
          <div className="premium-card premium-card-hover rounded-2xl p-6 md:p-8">
            <div className="flex items-start gap-4 mb-5 md:mb-6">
              <div className="w-12 md:w-14 h-12 md:h-14 rounded-xl bg-purple-400/10 border border-purple-400/20 flex items-center justify-center flex-shrink-0">
                <i className="ri-flashlight-fill text-2xl text-purple-400"></i>
              </div>
              <div>
                <h3 className="text-xl md:text-2xl font-bold text-white mb-1 font-heading">{t('profitMechanism.m2_title')}</h3>
                <p className="text-xs md:text-sm text-purple-400 font-medium">{t('profitMechanism.m2_sub')}</p>
              </div>
            </div>
            <p className="text-white/50 mb-5 leading-relaxed text-sm md:text-base">
              {t('profitMechanism.m2_desc')}
            </p>
            <div className="space-y-3 mb-5">
              {[
                { icon: 'ri-speed-line', titleKey: 'profitMechanism.m2_i0_title', subKey: 'profitMechanism.m2_i0_sub' },
                { icon: 'ri-shield-check-line', titleKey: 'profitMechanism.m2_i1_title', subKey: 'profitMechanism.m2_i1_sub' },
                { icon: 'ri-robot-line', titleKey: 'profitMechanism.m2_i2_title', subKey: 'profitMechanism.m2_i2_sub' },
              ].map(item => (
                <div key={item.titleKey} className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-purple-400/10 border border-purple-400/15 flex items-center justify-center flex-shrink-0">
                    <i className={`${item.icon} text-sm text-purple-400`}></i>
                  </div>
                  <div>
                    <div className="font-medium text-white text-sm">{t(item.titleKey)}</div>
                    <div className="text-xs text-white/35">{t(item.subKey)}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="bg-purple-500/8 border border-purple-500/18 rounded-xl p-4">
              <div className="text-xs mb-1 text-purple-400">{t('profitMechanism.m2_network_label')}</div>
              <div className="text-base font-bold text-white">{t('profitMechanism.m2_network_val')}</div>
            </div>
          </div>

          {/* Mechanism 3 */}
          <div className="premium-card premium-card-hover rounded-2xl p-6 md:p-8">
            <div className="flex items-start gap-4 mb-5 md:mb-6">
              <div className="w-12 md:w-14 h-12 md:h-14 rounded-xl bg-purple-400/10 border border-purple-400/20 flex items-center justify-center flex-shrink-0">
                <i className="ri-shield-star-line text-2xl text-purple-400"></i>
              </div>
              <div>
                <h3 className="text-xl md:text-2xl font-bold text-white mb-1 font-heading">{t('profitMechanism.m3_title')}</h3>
                <p className="text-xs md:text-sm text-purple-400 font-medium">{t('profitMechanism.m3_sub')}</p>
              </div>
            </div>
            <p className="text-white/50 mb-5 leading-relaxed text-sm md:text-base">
              {t('profitMechanism.m3_desc')}
            </p>
            <div className="space-y-3 mb-5">
              {[
                { icon: 'ri-shield-check-fill', titleKey: 'profitMechanism.m3_i0_title', descKey: 'profitMechanism.m3_i0_desc' },
                { icon: 'ri-line-chart-fill', titleKey: 'profitMechanism.m3_i1_title', descKey: 'profitMechanism.m3_i1_desc' },
                { icon: 'ri-shield-line', titleKey: 'profitMechanism.m3_i2_title', descKey: 'profitMechanism.m3_i2_desc' },
              ].map(item => (
                <div key={item.titleKey} className="bg-white/[0.03] border border-purple-500/8 rounded-xl p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <i className={`${item.icon} text-purple-400 text-sm`}></i>
                    <span className="font-bold text-white text-xs">{t(item.titleKey)}</span>
                  </div>
                  <p className="text-xs text-white/35">{t(item.descKey)}</p>
                </div>
              ))}
            </div>
            <div className="bg-white/[0.03] border border-purple-400/18 rounded-xl p-3 flex items-center gap-2">
              <i className="ri-error-warning-line text-purple-400 text-sm"></i>
              <span className="text-sm text-white/70 font-medium">{t('profitMechanism.m3_risk')}</span>
            </div>
          </div>
        </div>

        {/* Formula Visualization */}
        <div className="premium-card rounded-2xl p-6 md:p-10 border border-purple-400/12">
          <h3 className="text-xl md:text-2xl font-bold text-white mb-6 md:mb-8 text-center font-heading">{t('profitMechanism.formula_title')}</h3>
          <div className="flex flex-col md:flex-row items-center justify-center gap-5 md:gap-8">
            {[
              { label: t('profitMechanism.formula_marketPrice'), value: '0.60', sub: t('profitMechanism.formula_hiddenPct'), color: 'text-white/60' },
              { sep: '≠' },
              { label: t('profitMechanism.formula_aiTrue'), value: '72%', sub: t('profitMechanism.formula_evEdge'), color: 'text-purple-400' },
              { sep: '→' },
              { label: t('profitMechanism.formula_trigger'), value: t('profitMechanism.formula_arbitrage'), sub: t('profitMechanism.formula_autoExec'), color: 'text-gradient-aurora' },
            ].map((item, i) => 'sep' in item ? (
              <div key={i} className="text-3xl md:text-4xl text-purple-400/35 font-bold">{item.sep}</div>
            ) : (
              <div key={i} className="text-center">
                <div className="text-sm text-white/35 mb-2 font-medium">{item.label}</div>
                <div className={`text-4xl md:text-5xl font-bold mb-2 font-heading ${item.color}`}>{item.value}</div>
                <div className="text-sm text-purple-400/60 font-medium">{item.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
